import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { serverPHP } from '../const/consts';

// Gestionnaire global de z-index pour les modals
let currentModalZIndex = 1000;
const getNextModalZIndex = () => {
  currentModalZIndex += 10;
  return currentModalZIndex;
};

function Modal({ children, title, onClose, size = 'medium' }) {
  const zIndexRef = useRef(getNextModalZIndex());

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const modalSizeStyles = {
    small: { maxWidth: '400px' },
    medium: { maxWidth: '500px' },
    large: { maxWidth: '700px' },
    full: { maxWidth: '90vw' }
  };

  const modalContent = (
    <RemoveScroll>
      <div 
        className="modal fade-in-opacity" 
        onClick={onClose}
        style={{ zIndex: zIndexRef.current }}
      >
        <div 
          className="modal-content fade-in" 
          style={modalSizeStyles[size]} 
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title">{title}</h2>
            <button className="modal-close" onClick={onClose}>
              ✖️
            </button>
          </div>
          <div className="modal-body">
            {children}
          </div>
        </div>
      </div>
    </RemoveScroll>
  );

  return createPortal(modalContent, document.body);
}

function ConfirmModal({ children, title, onClose, onConfirm, confirmText = "Confirmer", cancelText = "Annuler" }) {
  const modalContent = (
    <Modal title={title} onClose={onClose} size="small">
      <div className="text-left">
        <div className="mb-4">
          {children}
        </div>
        <div className="flex gap-2 justify-center">
          <button className="btn btn-danger" onClick={() => { onConfirm(); onClose(); }}>
            {confirmText}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            {cancelText}
          </button>
        </div>
      </div>
    </Modal>
  );

  return modalContent;
}

function PodiumModal({ contributions, onClose, type, sessionInfo = null }) {
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [fetchedContributions, setFetchedContributions] = useState(contributions || []);
  const [loading, setLoading] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false); // État pour le feedback de copie
  const zIndexRef = useRef(getNextModalZIndex());

  // Si on a des infos de session, faire un fetch pour récupérer les données à jour
  useEffect(() => {
    const fetchLatestContributions = async () => {
      if (!sessionInfo) {
        setFetchedContributions(contributions || []);
        return;
      }
      
      setLoading(true);
      try {
        console.log('🏆 Fetching latest contributions for podium:', sessionInfo);
        
        const response = await fetch(`${serverPHP}getContributions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionInfo.sessionId,
            user_id: sessionInfo.userId,
            phase: sessionInfo.targetPhase,
            action: 'final'
          })
        });

        const data = await response.json();
        
        if (data.success && data.past_contributions) {
          const targetPhaseData = data.past_contributions.find(p => p.phase === sessionInfo.targetPhase);
          if (targetPhaseData && targetPhaseData.contributions) {
            console.log('🏆 Successfully fetched latest contributions:', targetPhaseData.contributions.length);
            setFetchedContributions(targetPhaseData.contributions.slice(0, 3));
          } else {
            console.log('🏆 No contributions found for target phase');
            setFetchedContributions(contributions || []);
          }
        } else {
          console.log('🏆 Failed to fetch contributions, using existing data');
          setFetchedContributions(contributions || []);
        }
      } catch (error) {
        console.error('🏆 Error fetching latest contributions:', error);
        setFetchedContributions(contributions || []);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestContributions();
  }, [sessionInfo, contributions]);

  if ((!fetchedContributions || fetchedContributions.length === 0) && !loading) return null;

  const topThree = (fetchedContributions || []).slice(0, 3);
  
  const getMedalEmoji = (index) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return '🏆';
    }
  };

  const getMedalClass = (index) => {
    switch (index) {
      case 0: return 'gold';
      case 1: return 'silver';
      case 2: return 'bronze';
      default: return '';
    }
  };

  const getPositionClass = (index) => {
    switch (index) {
      case 0: return 'first';
      case 1: return 'second';
      case 2: return 'third';
      default: return '';
    }
  };

  const getEvaluationEmoji = (type) => {
    switch (type) {
      case 'moinsdeux': return '😡';
      case 'moinsun': return '😕';
      case 'zero': return '😐';
      case 'un': return '🙂';
      case 'deux': return '😊';
      default: return '❓';
    }
  };

  const showContributionDetails = (contribution) => {
    setSelectedContribution(contribution);
  };

  // --- FONCTION DE COPIE POUR LA MODAL ---
  const handleCopyContribution = async (texteToCopy) => {
    if (copyFeedback) return; // Empêche le spam-clic

    console.log('🔄 Copie dans la modal - Texte :', texteToCopy.substring(0, 50) + '...');

    if (!texteToCopy || texteToCopy.trim() === '') {
      console.error('❌ Aucun texte à copier');
      return;
    }

    try {
      // Vérifier si l'API Clipboard est disponible
      if (navigator.clipboard && window.isSecureContext) {
        console.log('📋 Utilisation de l\'API Clipboard moderne');
        await navigator.clipboard.writeText(texteToCopy);
        
        console.log('✅ Copie réussie avec clipboard API');
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
        
      } else {
        console.log('📋 Fallback vers l\'ancienne méthode de copie');
        
        // Fallback pour les navigateurs plus anciens ou HTTP
        const textArea = document.createElement('textarea');
        textArea.value = texteToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            console.log('✅ Copie réussie avec execCommand');
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000);
          } else {
            throw new Error('execCommand a échoué');
          }
        } catch (fallbackErr) {
          document.body.removeChild(textArea);
          throw fallbackErr;
        }
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de la copie :', error);
      
      // Feedback d'erreur temporaire
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  };

  // Ordre inversé pour les évaluations (très positif → très négatif)
  const evaluationOrder = ['deux', 'un', 'zero', 'moinsun', 'moinsdeux'];
  const evaluationLabels = {
    'deux': 'Très positif',
    'un': 'Positif', 
    'zero': 'Neutre',
    'moinsun': 'Négatif',
    'moinsdeux': 'Très négatif'
  };

  const modalContent = (
    <RemoveScroll>
      <div 
        className="podium-modal" 
        onClick={onClose}
        style={{ zIndex: zIndexRef.current }}
      >
        <div className="podium-content" onClick={(e) => e.stopPropagation()}>
          <button className="podium-close" onClick={onClose}>
            ✕
          </button>
          
          <div className="podium-header">
            <h2 className="podium-title">🏆 Podium des contributions</h2>
            <p className="podium-subtitle">Les meilleures formulations retenues</p>
          </div>

          {loading ? (
            <></>
          ) : (
            <div className="podium-grid">
              {topThree.map((contribution, index) => (
                <div 
                  key={index} 
                  className={`podium-item ${getPositionClass(index)} cursor-pointer`}
                  onClick={() => showContributionDetails(contribution)}
                >
                  <span className={`podium-medal ${getMedalClass(index)}`}>
                    {getMedalEmoji(index)}
                  </span>
                  <p className="podium-text">
                    {contribution.contribution}
                  </p>
                  <div className="podium-score">
                    Score: {contribution.totalValue || 0}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </RemoveScroll>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Modal de détails avec bouton copier */}
      {selectedContribution && (
        <Modal 
          title="📊 Détail des évaluations" 
          onClose={() => setSelectedContribution(null)}
        >
          <div className="mb-4">
            {/* Header avec titre et bouton copier */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Contribution :</h3>
              <button
                onClick={() => handleCopyContribution(selectedContribution.contribution)}
                className={`btn-action-compact ${
                  copyFeedback === true ? 'btn-action-success' : 
                  copyFeedback === 'error' ? 'btn-action-error' : ''
                }`}
                title="Copier la contribution"
                disabled={!selectedContribution.contribution || copyFeedback === true}
                              style={{width:'auto',minWidth:'32px'}}

              >
                {copyFeedback === true ? '✅' : 
                 copyFeedback === 'error' ? '❌' : '📋'}
                
                <span className="hidden md:inline ml-2" style={{paddingRight:'0.5rem'}}>
                  {copyFeedback === true ? 'Copié !' : 
                   copyFeedback === 'error' ? 'Erreur' : 'Copier'}
                </span>
              </button>
            </div>
            
            {/* Texte de la contribution */}
            <p className="bg-secondary p-3 rounded">{selectedContribution.contribution}</p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Score total : {selectedContribution.totalValue || 0}</h3>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Répartition des évaluations :</h3>
            <div className="space-y-2">
              {evaluationOrder.map((noteType) => (
                <div key={noteType} className="flex items-center justify-between p-2 bg-secondary rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getEvaluationEmoji(noteType)}</span>
                    <span className="capitalize">
                      {evaluationLabels[noteType]}
                    </span>
                  </div>
                  <span className="font-bold">{selectedContribution.notes?.[noteType] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default Modal;
export { ConfirmModal, PodiumModal };